import { Camera, Globe, Music2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/cn";
import type { Client } from "@/types";

type SocialLink = {
  key: "facebook" | "instagram" | "tiktok";
  label: string;
  url: string | undefined;
  Icon: typeof Globe;
};

function toExternalUrl(value: string | undefined): string | null {
  const raw = value?.trim();
  if (!raw) return null;
  const candidate = /^https?:\/\//i.test(raw) ? raw : `https://${raw.replace(/^\/+/, "")}`;
  try {
    const parsed = new URL(candidate);
    return parsed.protocol === "http:" || parsed.protocol === "https:" ? parsed.toString() : null;
  } catch {
    return null;
  }
}

export function ClientSocialLinks({
  client,
  compact = false,
  showEmpty = false,
  className,
}: {
  client: Client;
  compact?: boolean;
  showEmpty?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const links: SocialLink[] = [
    { key: "facebook", label: t("clients.facebook"), url: client.facebookUrl, Icon: Globe },
    { key: "instagram", label: t("clients.instagram"), url: client.instagramUrl, Icon: Camera },
    { key: "tiktok", label: t("clients.tiktok"), url: client.tiktokUrl, Icon: Music2 },
  ];
  const available = links
    .map((link) => ({ ...link, href: toExternalUrl(link.url) }))
    .filter((link): link is SocialLink & { href: string } => Boolean(link.href));

  if (available.length === 0) {
    return showEmpty ? <span className="text-xs text-text3">{t("clients.noSocialLinks")}</span> : <span className="text-text3">-</span>;
  }

  return (
    <div className={cn("flex items-center gap-1.5", className)}>
      {available.map(({ key, label, href, Icon }) => (
        <a
          key={key}
          href={href}
          target="_blank"
          rel="noreferrer"
          title={t("clients.openSocialProfile", { platform: label })}
          aria-label={t("clients.openSocialProfile", { platform: label })}
          className={cn(
            "inline-flex h-7 w-7 items-center justify-center rounded-sm border border-border bg-bg2 text-text2 transition-colors hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
            compact && "h-6 w-6",
          )}
          onClick={(event) => event.stopPropagation()}
        >
          <Icon className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} />
        </a>
      ))}
    </div>
  );
}
